// src/pages/auth/Login.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { authService } from '../../services/auth';
import toast from 'react-hot-toast';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { getAssetUrl } from '../../utils/url';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [logoError, setLogoError] = useState(false);

  const { setUser, setToken } = useAuthStore();
  const { settings, fetchPublicSettings } = useSystemSettingsStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPublicSettings();
  }, [fetchPublicSettings]);

  useEffect(() => {
    setLogoError(false);
  }, [settings.logo_url]);

  const appName = settings.app_name || 'SIM-EVAL';
  const appDescription = settings.app_description || 'Sistem Informasi Manajemen Evaluasi';
  const institutionName = settings.institution_name || 'Universitas Buana Perjuangan Karawang';
  const institutionAddress = settings.institution_address || '';
  const institutionPhone = settings.institution_phone || '';
  const institutionEmail = settings.institution_email || '';
  const institutionWebsite = settings.institution_website || '';
  const footerText = settings.footer_text || '© 2026 SIM-EVAL UBP Karawang. All rights reserved.';

  const logoUrl = getAssetUrl(settings.logo_url);
  const showLogo = logoUrl && !logoError;

  const brandInitials = appName
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

  useEffect(() => {
    if (appName) {
      document.title = appName;
    }
    if (settings.favicon_url) {
      const faviconUrl = getAssetUrl(settings.favicon_url);
      if (faviconUrl) {
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = faviconUrl;
      }
    }
  }, [appName, settings.favicon_url]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!email) {
      setErrors(prev => ({ ...prev, email: 'Email wajib diisi' }));
      return;
    }
    if (!password) {
      setErrors(prev => ({ ...prev, password: 'Password wajib diisi' }));
      return;
    }

    setLoading(true);
    try {
      const result = await authService.login(email, password);
      const { user, token } = result;

      setToken(token);
      setUser(user);

      toast.success(`Selamat datang, ${user.name}!`);
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Login failed:', err);

      if (err.response?.status === 422) {
        const validationErrors = err.response.data?.errors || {};
        setErrors(validationErrors);
        toast.error(err.response.data?.message || 'Silakan periksa data yang Anda masukkan');
      } else if (err.response?.status === 401) {
        setErrors({ email: 'Email atau password tidak cocok' });
        toast.error('Email atau password tidak cocok');
      } else if (err.response?.status === 419) {
        toast.error('Session expired. Silakan refresh halaman dan coba lagi.');
        window.location.reload();
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        toast.error('Tidak dapat terhubung ke server. Periksa koneksi internet Anda.');
      } else {
        toast.error(err.response?.data?.message || err.message || 'Terjadi kesalahan. Silakan coba lagi.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-dark via-teal-main to-teal-light flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo Card */}
        <div className="text-center mb-8">
          <div
            className={`inline-flex items-center justify-center w-16 h-16 mb-4 animate-bounce overflow-hidden rounded-3xl ${showLogo ? 'bg-transparent' : 'bg-white shadow-2xl'
              }`}
          >
            {showLogo ? (
              <img
                src={logoUrl!}
                alt="Logo"
                className="w-full h-full object-contain"
                onError={() => setLogoError(true)}
              />
            ) : (
              <span className="text-3xl font-black bg-gradient-to-r from-teal-dark to-teal-main bg-clip-text text-transparent">
                {brandInitials}
              </span>
            )}
          </div>
          <h1 className="text-4xl font-black text-white tracking-tight drop-shadow-lg">{appName}</h1>
          <p className="text-sm text-white/80 mt-2 font-medium">{appDescription}</p>
          <p className="text-xs text-white/70 mt-1">{institutionName}</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 backdrop-blur-sm">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-black text-teal-dark">Masuk ke Sistem</h2>
            <p className="text-sm text-neutral-text-secondary mt-2">Silakan masukkan email dan password Anda</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div className="space-y-2">
              <label className="block text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
                Email
              </label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-teal-dark transition-colors group-focus-within:text-teal-main" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="contoh@ubpkarawang.ac.id"
                  className={`
                    w-full pl-12 pr-4 py-3 rounded-xl border-2 text-sm font-medium
                    bg-white
                    focus:outline-none focus:ring-2 focus:ring-teal-light transition-all duration-200
                    ${errors.email ? 'border-danger' : 'border-neutral-border focus:border-teal-light'}
                  `}
                />
              </div>
              {errors.email && <p className="text-xs text-danger font-semibold">{errors.email}</p>}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="block text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
                Password
              </label>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-teal-dark transition-colors group-focus-within:text-teal-main" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Masukkan password"
                  className={`
                    w-full pl-12 pr-12 py-3 rounded-xl border-2 text-sm font-medium
                    bg-white
                    focus:outline-none focus:ring-2 focus:ring-teal-light transition-all duration-200
                    ${errors.password ? 'border-danger' : 'border-neutral-border focus:border-teal-light'}
                  `}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-teal-main hover:text-teal-dark transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-danger font-semibold">{errors.password}</p>}
            </div>

            {/* Remember & Forgot */}
            <div className="flex items-center justify-between pt-2">
              <label className="flex items-center gap-2 text-sm text-neutral-text-secondary cursor-pointer hover:text-neutral-text-primary transition-colors">
                <input type="checkbox" className="rounded border-neutral-border text-teal-main focus:ring-teal-light cursor-pointer" />
                <span className="font-medium">Ingat saya</span>
              </label>
              <a href="/forgot-password" className="text-sm font-bold text-teal-main hover:text-teal-light transition-colors">
                Lupa password?
              </a>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className={`
                w-full py-3 rounded-xl font-bold text-sm text-white
                bg-gradient-to-r from-teal-dark to-teal-main
                hover:from-teal-dark hover:to-teal-light
                shadow-lg shadow-teal-main/40 hover:shadow-xl hover:shadow-teal-main/50
                focus:outline-none focus:ring-2 focus:ring-teal-light focus:ring-offset-2
                transition-all duration-300
                ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}
              `}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Memproses...
                </span>
              ) : (
                'Masuk'
              )}
            </button>
          </form>

          {/* Institusi Info */}
          {(institutionAddress || institutionPhone || institutionEmail || institutionWebsite) && (
            <div className="mt-8 pt-6 border-t border-neutral-border">
              <p className="text-xs text-neutral-text-secondary text-center font-semibold mb-3">Alamat:</p>
              <div className="text-center text-xs text-black/70 space-y-1">
                {institutionAddress && <p>{institutionAddress}</p>}
                {(institutionPhone || institutionEmail) && (
                  <p>{[institutionPhone, institutionEmail].filter(Boolean).join(' • ')}</p>
                )}
                {institutionWebsite && <p>{institutionWebsite}</p>}
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-white/50 mt-8 font-medium">
          {footerText}
        </p>
      </div>
    </div>
  );
};

export default Login;
